/* eslint-disable react/jsx-handler-names */
/* eslint-disable max-lines */
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { motion, useReducedMotion } from "motion/react";
import SearchBar from "../header/searchBar";
import { fetchSearchAutoSuggestions, fetchSearchSuggestions } from "../../../api/common";
import { debounce, formatClassNames, pageName } from "../../../utils/common";
import { plpPageConstants } from "../../../constants/pageConstants/plpConstants";
import {
  COUNTRIES,
  DEBOUNCE_TIME,
  MAX_COUNT_RECENT_SEARCH,
  RECENT_SEARCH_LIST,
} from "../../../constants/common/common";
import { usePathname } from "next/navigation";
import { getProductListingDetailsAPI } from "../../../api/plpApi";
import CookieUtil from "../../../utils/storage/cookieUtil";
import { getLocalStorageItem, setLocalStorageItem } from "../../../utils/storage/localStorageUtil";
import { cleverTapCommonEvents } from "../../../analytics/clevertapConstants";
import useAnalytics from "../../../analytics/useAnalytics";
import isEmpty from "lodash.isempty";
import { shallowEqual, useSelector } from "react-redux";
import { GA4_EVENTS } from "../../../analytics/gaConstant";
import DefaultSearchPage from "../defaultSearchPage/DefaultSearchPage";
import {
  DESKTOP_DEFAULT_SEARCH_CURTAIN_TRANSITION,
  DESKTOP_DEFAULT_SEARCH_EMPTY_HEIGHT_PX,
  DESKTOP_DEFAULT_SEARCH_EXPANDED_HEIGHT,
  DESKTOP_DEFAULT_SEARCH_HEIGHT_TRANSITION,
  DESKTOP_SEARCH_DROPDOWN_SHELL_CLASS,
  DEFAULT_SEARCH_HIDDEN_SCROLLBAR_CLASS,
} from "../defaultSearchPage/constants";
import { unlockBody } from "../../../utils/document";
import Suggestions from "./searchComponents/suggestions";
const AutoSuggestions = dynamic(() => import("./autoSuggestions"), { ssr: false });
const AiGiftFinderIsland = dynamic(() => import("../../../containers/aiGiftFinder/views/AiGiftFinderIsland"), {
  ssr: false,
});

/**
 * DesktopSearch component handles the search bar, search suggestions,
 * and recent searches for the desktop view.
 * It manages user input, debounces API calls, and updates localStorage for recent searches.
 *
 * @component
 */
const DesktopSearch = ({
  catalogId,
  searchQuery,
  setIsSearchFocused,
  defaultSearchWidgets,
  onRequestDefaultSearchFeed,
  isDefaultLoading,
  isAutoSuggestion,
  isFromPDP = false,
  isSearchFocused = false,
  isGiftFinderEnabled = true,
}) => {
  const pathname = usePathname();
  const reduceMotion = useReducedMotion();
  const { trackEvent } = useAnalytics();
  const [defaultSearchCompactHeightPx, setDefaultSearchCompactHeightPx] = useState(
    DESKTOP_DEFAULT_SEARCH_EMPTY_HEIGHT_PX,
  );
  const [inputValue, setInputValue] = useState(searchQuery || "");
  const [searchSuggestion, setSearchSuggestion] = useState(null);
  const [recentSearch, setRecentSearch] = useState([]);
  const [recentSearchCount, setRecentSearchCount] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const completePathName = typeof window !== "undefined" && window.location.href ? window.location.href : null;

  const { pincode, domesticAddress, selectedIntLocation, pincodeIntl } = useSelector(
    (state) => ({
      pincode: state?.locationLock?.selectedDomesticLocation?.pincode,
      domesticAddress: state?.locationLock?.selectedDomesticLocation?.address,
      selectedIntLocation: state?.locationLock?.selectedIntLocation?.location,
      pincodeIntl: state?.locationLock?.selectedIntLocation?.pincode,
    }),
    shallowEqual,
  );

  const updatedPincode = catalogId !== COUNTRIES.INDIA ? pincodeIntl : pincode;

  const searchEventCommonData = {
    country: catalogId,
    city: catalogId !== COUNTRIES.INDIA ? selectedIntLocation : "",
    area: catalogId !== COUNTRIES.INDIA ? "" : domesticAddress || "",
    pincode: catalogId !== COUNTRIES.INDIA ? "" : pincode || "",
    system_pincode: catalogId !== COUNTRIES.INDIA ? "" : pincode || "",
  };

  const isHome = pathname === "/";
  const isPLP = pathname?.includes("-lp");
  const isSearch = pathname?.includes("/search");
  const { product } = useSelector(
    (state) => ({
      product: state?.pdp?.clientCurrentClickedItem,
    }),
    shallowEqual,
  );
  const urlIdentifier = product?.urlIdentifier;

  const defaultSearchPageRef = useRef(null);

  const hasDefaultSearchFeedWidgets = defaultSearchWidgets?.length > 0;
  const isDefaultSearchFeedLoading = isDefaultLoading && !hasDefaultSearchFeedWidgets;
  const isDefaultSearchDropdownExpanded = isDefaultSearchFeedLoading || hasDefaultSearchFeedWidgets;

  /** @param {{ isExpanded: boolean, compactHeightPx: number }} layout */
  const handleDefaultSearchPanelLayout = useCallback(({ compactHeightPx }) => {
    setDefaultSearchCompactHeightPx(compactHeightPx);
  }, []);

  const defaultSearchDropdownShellClass = formatClassNames(
    DESKTOP_SEARCH_DROPDOWN_SHELL_CLASS,
    (isFromPDP || isSearch) && "xmll:left-[23px] xll:left-[64px] xxl:left-140",
  );

  const defaultSearchDropdownTransition = reduceMotion
    ? { duration: 0 }
    : {
        height: DESKTOP_DEFAULT_SEARCH_HEIGHT_TRANSITION,
        clipPath: DESKTOP_DEFAULT_SEARCH_CURTAIN_TRANSITION,
      };

  useEffect(() => {
    /**to check if the click is made outsude the popover.*/
    const handleOutSideClick = (event) => {
      if (!defaultSearchPageRef.current?.contains(event.target)) {
        setIsSearchFocused(false);
        setIsFocused(false);
        if (isSearchFocused) {
          unlockBody();
        }
      }
    };

    window.addEventListener("mousedown", handleOutSideClick);

    return () => {
      window.removeEventListener("mousedown", handleOutSideClick);
    };
  }, [defaultSearchPageRef, isSearchFocused]);

  // Load recent searches from localStorage on mount
  useEffect(() => {
    const searchHistory = getLocalStorageItem(RECENT_SEARCH_LIST);
    const searchHistoryRecentSearchList = searchHistory && JSON.parse(searchHistory);
    searchHistory && setRecentSearch(searchHistoryRecentSearchList?.recentSearchList);
  }, []);

  // Debounced version of the search function to fetch suggestions
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchSuggestions = useCallback(
    debounce(async (value) => {
      if (value.trim().length <= 2) return;
      if (isAutoSuggestion) {
        try {
          setIsLoading(true);
          const response = await fetchSearchAutoSuggestions(catalogId, value, updatedPincode);
          setSearchSuggestion(response);
          setRecentSearchCount(response?.recentSearchCount);
        } catch (error) {
          console.error("Error fetching search suggestions:", error);
          setSearchSuggestion(null);
        } finally {
          setIsLoading(false);
        }
      } else {
        const response = await fetchSearchSuggestions(catalogId, value, 1234);
        response?.suggestionResponse && setSearchSuggestion(response?.suggestionResponse);
        response?.recentSearchCount && setRecentSearchCount(response?.recentSearchCount);
      }
    }, DEBOUNCE_TIME),
    [isAutoSuggestion, updatedPincode],
  );

  useEffect(() => {
    setIsLoading(false);
    setSearchSuggestion(null);
  }, [searchQuery]);

  /**
   * Handles search input changes and triggers the suggestion fetch.
   *
   * @param {Event} event - The input change event.
   */
  const onSearch = async (event) => {
    const value = event.target.value;
    setIsLoading(true);
    setInputValue(value);
    if (value.length > 0) {
      debouncedFetchSuggestions(value);
    } else {
      debouncedFetchSuggestions(value);
      setSearchSuggestion(null);
    }
  };
  /**
   * Clears the current search input and suggestions.
   */
  const onClearSearch = () => {
    setInputValue("");
    setSearchSuggestion(null);
  };
  /**
   * Handles focus on the input field.
   */
  const onFocus = () => {
    setIsFocused(true);
    onRequestDefaultSearchFeed?.();
  };

  /**
   * Handles blur (focus out) on the input field.
   */
  const onBlur = () => {
    if (!isAutoSuggestion) {
      setTimeout(() => {
        setIsFocused(false);
      }, 200);
    }
  };

  /**
   * Adds a search keyword to the recent search list in localStorage.
   *
   * @param {string} keyword - The search keyword.
   * @param {string} url - The URL associated with the search keyword.
   */
  const addRecentSearchKeyword = (keyword, url) => {
    const searchHistory = getLocalStorageItem(RECENT_SEARCH_LIST);
    let recentSearchList = searchHistory ? JSON.parse(searchHistory) : { recentSearchList: [] };
    // Remove the existing object with the same keyword and URL, if it exists
    recentSearchList.recentSearchList = recentSearchList.recentSearchList.filter(
      (item) => item.keyword !== keyword || item.url !== url,
    );
    // Check if the list exceeds 8 items, remove the oldest one
    if (recentSearchList.recentSearchList.length >= MAX_COUNT_RECENT_SEARCH) {
      recentSearchList.recentSearchList.shift();
    }
    // Add the new search entry
    recentSearchList.recentSearchList.push({
      keyword: keyword,
      url: url,
      suggestionResponseType: "RS",
    });
    // Update localStorage
    setLocalStorageItem(RECENT_SEARCH_LIST, JSON.stringify(recentSearchList));
  };

  /** */
  const onSearchBarClick = () => {
    if (inputValue?.length > 0) {
      setIsSearchFocused(true);
    }
    const searchSelectedData = {
      page_name: pageName(isHome, isPLP, pathname, urlIdentifier),
      pageType: (typeof window !== "undefined" && window.fnpPageType) || "",
      complete_url: completePathName,
      clean_url: pathname,
    };
    trackEvent(cleverTapCommonEvents.searchSelected, searchSelectedData, ["clevertap"]);
  };
  /**
   * Handles the form submission for searching and redirects to the product listing page.
   *
   * @param {Event} e - The form submit event.
   */
  /**
   * Closes the search dropdown/popover
   */
  const closeSearchDropdown = () => {
    setIsFocused(false);
    setIsSearchFocused(false);
    unlockBody();
  };

  /**
   *
   * @param {*} e
   * @returns
   */
  const onSubmit = async (e) => {
    e.preventDefault();

    if (inputValue === searchQuery || isEmpty(inputValue)) {
      return;
    }

    setIsLoading(true);
    closeSearchDropdown();

    const pageListingData = {
      page: plpPageConstants.INITIAL_PAGE,
      size: plpPageConstants.DESKTOP_PRODUCT_PER_PAGE,
      geoId: catalogId,
      isFromApp: "yes",
      pageType: "search",
      qs: inputValue,
      fnpId: CookieUtil.getCookie("ab_user_id"),
    };
    const res = await getProductListingDetailsAPI(pageListingData);
    if (res?.redirectUrl) {
      const url = new URL(res.redirectUrl);
      const relativePath = `${url?.pathname}${url?.search}`;
      addRecentSearchKeyword(inputValue, res.redirectUrl);
      window.location.href = relativePath;
    } else {
      const searchUrl = `/search?FNP_CURRENT_CATALOG_ID=${catalogId}&qs=${inputValue}`;
      addRecentSearchKeyword(inputValue, searchUrl);
      window.location.href = searchUrl;
    }

    const clevertapSearchData = {
      ...searchEventCommonData,
      search: inputValue,
      is_popular_search: false,
      is_recent_search: false,
      is_manual_boost: false,
    };
    trackEvent(cleverTapCommonEvents.searched, clevertapSearchData, ["clevertap"]);
    trackEvent(GA4_EVENTS?.SEARCH, { search: inputValue || null }, ["ga4"]);
  };

  const combinedAutoSearch = {
    recentSearch: Array.isArray(recentSearch) ? recentSearch.slice(-recentSearchCount) : [],
    searchSuggestion: searchSuggestion || {},
  };
  const combinedSearch = [
    ...(Array.isArray(recentSearch) ? recentSearch.slice(-recentSearchCount) : []),
    ...(Array.isArray(searchSuggestion) ? searchSuggestion : []),
  ];

  return (
    <div className="flex w-full items-center justify-between gap-12" ref={defaultSearchPageRef}>
      <form className="w-full max-w-[652px] shrink-0" onSubmit={onSubmit}>
        <SearchBar
          isShowStartAdornment={true}
          inputValue={inputValue}
          onChange={onSearch}
          onClearSearch={onClearSearch}
          onSearchBarClick={onSearchBarClick}
          isShowEndAdornment={true}
          isMobile={false}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      </form>
      {/* After the form so the pill sits to the right of the search bar on desktop. */}
      {isGiftFinderEnabled && (
        <AiGiftFinderIsland
          launcherClassName="inline-flex h-[50px] flex-none px-12"
          isMobile={false}
          catalogId={catalogId}
        />
      )}
      {/* Show skeleton when loading, AutoSuggestions when data is available */}
      {isAutoSuggestion
        ? isFocused &&
          (inputValue.length >= 3 ? (
            <div className={defaultSearchDropdownShellClass}>
              <AutoSuggestions
                combinedSearch={combinedAutoSearch}
                searchEventCommonData={searchEventCommonData}
                inputValue={inputValue}
                isLoading={isLoading}
                setIsSearchFocused={setIsSearchFocused}
                isMobile={false}
                onNavigate={closeSearchDropdown}
              />
            </div>
          ) : (
            <motion.div
              className={formatClassNames(defaultSearchDropdownShellClass, DEFAULT_SEARCH_HIDDEN_SCROLLBAR_CLASS)}
              initial={reduceMotion ? false : { clipPath: "inset(0 0 100% 0 round 0px)" }}
              animate={{
                height: isDefaultSearchDropdownExpanded
                  ? DESKTOP_DEFAULT_SEARCH_EXPANDED_HEIGHT
                  : `${defaultSearchCompactHeightPx}px`,
                clipPath: "inset(0 0 0% 0 round 0px)",
              }}
              transition={defaultSearchDropdownTransition}
              style={{
                transformOrigin: "top center",
                maxHeight: isDefaultSearchDropdownExpanded ? DESKTOP_DEFAULT_SEARCH_EXPANDED_HEIGHT : undefined,
              }}
            >
              <DefaultSearchPage
                widgets={defaultSearchWidgets}
                isMobile={false}
                isFeedLoading={isDefaultLoading}
                onDesktopPanelLayout={handleDefaultSearchPanelLayout}
                setIsSearchFocused={setIsSearchFocused}
                onNavigate={closeSearchDropdown}
              />
            </motion.div>
          ))
        : combinedSearch?.length > 0 &&
          isFocused && (
            <Suggestions
              searchSuggestion={combinedSearch}
              searchEventCommonData={searchEventCommonData}
              isFromPDP={isFromPDP}
              isSearch={isSearch}
              onClose={closeSearchDropdown}
              onNavigate={closeSearchDropdown}
            />
          )}
    </div>
  );
};
DesktopSearch.propTypes = {
  catalogId: PropTypes.string,
  searchQuery: PropTypes.string,
  setIsSearchFocused: PropTypes.func,
  defaultSearchWidgets: PropTypes.arrayOf(PropTypes.object),
  onRequestDefaultSearchFeed: PropTypes.func,
  isDefaultLoading: PropTypes.bool,
  isFromPDP: PropTypes.bool,
  isAutoSuggestion: PropTypes.bool,
  isSearchFocused: PropTypes.bool,
  isGiftFinderEnabled: PropTypes.bool,
};
export default DesktopSearch;
